import { useState, useEffect } from "react";
import { RichTextEditor } from "../../../../components/Editor/RichTextEditor";
import { Button } from "../../../../components/ui/button";
import {
  Plus,
  X,
  Edit,
  Trash2,
  Eye,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "react-feather";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner, SpinnerBlock } from "@/components/ui/spinner";
import type {
  ResourceFormData,
  ResourceResponse,
  PaginationInfo,
} from "../../../../types/resource";
import { resourceService } from "../../../../services/resource.service";
import { toast } from "react-hot-toast";
import { ConfirmationModal } from "../../../../components/ui/confirmation-modal";
import { AxiosError } from "axios";
import { getRichTextLimitError } from "@/utils/richText";

// Function to process content and convert YouTube links to iframes (same as FAQ)
function processYouTubeContent(content: string): string {
  if (!content) return content;

  // First, handle YouTube links that are wrapped in <a> tags by TipTap
  let processedContent = content.replace(
    /<a[^>]*href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))"[^>]*>.*?<\/a>/g,
    (_, _url, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Then handle any plain YouTube URLs that might not be in links
  processedContent = processedContent.replace(
    /(?<!href=["'])https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?!["'])/g,
    (_, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  // Also wrap existing iframes in our wrapper for consistent styling
  processedContent = processedContent.replace(
    /<iframe([^>]*youtube[^>]*)><\/iframe>/g,
    '<div class="youtube-embed-wrapper"><iframe$1></iframe></div>'
  );

  return processedContent;
}

function formatDate(dateString: string): string {
  return resourceService.formatDate(dateString);
}

export function ShareResources() {
  const [resources, setResources] = useState<ResourceResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingResource, setEditingResource] = useState<
    ResourceResponse | ResourceFormData | null
  >(null);
  const [previewResource, setPreviewResource] =
    useState<ResourceResponse | null>(null);
  const [formData, setFormData] = useState<ResourceFormData>({
    title: "",
    content: "",
  });
  const [errors, setErrors] = useState<Partial<ResourceFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    resourceId: string | null;
    resourceTitle: string;
    isLoading: boolean;
  }>({
    isOpen: false,
    resourceId: null,
    resourceTitle: "",
    isLoading: false,
  });

  // Load resources on component mount
  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async (page: number = 1) => {
    try {
      setLoading(true);
      const response = await resourceService.getResourcesForBrand(
        page,
        pagination.limit
      );
      setResources(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error loading resources:", error);
      toast.error("Failed to load resources. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadResources(newPage);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ResourceFormData> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    } else {
      const contentError = getRichTextLimitError(formData.content, 7000);
      if (contentError) {
        newErrors.content = contentError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddResource = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const newResource = await resourceService.createResource(formData);
      setResources((prev) => [newResource, ...prev]);
      setShowAddForm(false);
      resetForm();
      toast.success("Resource added successfully!");
      // Reload the current page to refresh the list
      loadResources(pagination.page);
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.status === 400 &&
        error.response?.data?.message.length > 0 &&
        error.response?.data?.statusCode === 400
      ) {
        error.response.data.message.forEach((message: string) => {
          toast.error(message);
        });
      } else {
        console.error("Error adding resource:", error);
        toast.error("Failed to add resource. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditResource = async () => {
    if (!editingResource || !validateForm()) return;

    try {
      setIsSubmitting(true);
      const resourceId = (editingResource as ResourceResponse).id;
      const updatedResource = await resourceService.updateResource(
        resourceId,
        formData
      );

      setResources((prev) =>
        prev.map((resource) =>
          resource.id === resourceId ? updatedResource : resource
        )
      );
      setEditingResource(null);
      resetForm();
      toast.success("Resource updated successfully!");
    } catch (error) {
      console.error("Error updating resource:", error);
      toast.error("Failed to update resource. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (resourceId: string, resourceTitle: string) => {
    setDeleteModal({
      isOpen: true,
      resourceId,
      resourceTitle,
      isLoading: false,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      resourceId: null,
      resourceTitle: "",
      isLoading: false,
    });
  };

  const handleDeleteResource = async () => {
    if (!deleteModal.resourceId) return;

    try {
      setDeleteModal((prev) => ({ ...prev, isLoading: true }));
      await resourceService.deleteResource(deleteModal.resourceId);
      setResources((prev) =>
        prev.filter((resource) => resource.id !== deleteModal.resourceId)
      );
      toast.success("Resource deleted successfully!");
      closeDeleteModal();
      // Reload the current page to refresh the list
      loadResources(pagination.page);
    } catch (error) {
      console.error("Error deleting resource:", error);
      toast.error("Failed to delete resource. Please try again.");
      setDeleteModal((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
    });
    setErrors({});
  };

  const openEditForm = (resource: ResourceFormData) => {
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      content: resource.content,
    });
  };

  const closeForm = () => {
    setShowAddForm(false);
    setEditingResource(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Share Resources
          </h2>
        </div>
        <SpinnerBlock label="Loading resources" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">
          Share Resources
        </h2>
        <Button
          id="SR-add-resource-button"
          onClick={() => setShowAddForm(true)}
        >
          <Plus className="w-4 h-4" />
          Add New Resource
        </Button>
      </div>

      {/* Resources List */}
      <div id="SR-resources-list" className="space-y-4">
        {resources.length > 0 &&
          resources.map((resource) => (
            <div
              key={resource.id}
              className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-brand-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {resource.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>•</span>
                      <span>{formatDate(resource.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewResource(resource)}
                    className="p-2 text-muted-foreground hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEditForm(resource)}
                    className="p-2 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(resource.id, resource.title)}
                    className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Preview of content */}
              <div className="prose max-w-none">
                <div
                  dangerouslySetInnerHTML={{
                    __html: processYouTubeContent(
                      resource.content.substring(0, 200) +
                        (resource.content.length > 200 ? "..." : "")
                    ),
                  }}
                  className="text-muted-foreground"
                />
              </div>
            </div>
          ))}

        {resources.length === 0 && (
          <EmptyState
            icon={<FileText />}
            title="No resources yet"
            body="Add your first resource to share with affiliates"
          />
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border bg-card px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium">
                  {(pagination.page - 1) * pagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}
                </span>{" "}
                of <span className="font-medium">{pagination.total}</span>{" "}
                results
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-muted-foreground ring-1 ring-inset ring-border hover:bg-accent focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>

                {/* Page numbers */}
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === pagination.totalPages ||
                      Math.abs(page - pagination.page) <= 1
                    );
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const showEllipsisBefore =
                      index > 0 && array[index - 1] !== page - 1;

                    return (
                      <div key={page} className="flex items-center">
                        {showEllipsisBefore && (
                          <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-muted-foreground ring-1 ring-inset ring-border">
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-border focus:z-20 focus:outline-offset-0 ${
                            page === pagination.page
                              ? "z-10 bg-brand-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                              : "text-foreground hover:bg-accent"
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    );
                  })}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-muted-foreground ring-1 ring-inset ring-border hover:bg-accent focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {(showAddForm || editingResource) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-foreground">
                {editingResource ? "Edit Resource" : "Add New Resource"}
              </h2>
              <button
                onClick={closeForm}
                className="text-muted-foreground hover:text-muted-foreground"
                disabled={isSubmitting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div id="SR-resource-title-field">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-muted-foreground mb-2"
                >
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                    errors.title ? "border-red-500" : "border-border"
                  }`}
                  placeholder="Enter resource title"
                  disabled={isSubmitting}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Content */}
              <div id="SR-resource-content-field">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Content *
                </label>
                <RichTextEditor
                  content={formData.content}
                  onChange={(content) =>
                    setFormData((prev) => ({ ...prev, content }))
                  }
                  placeholder="Write your content here. You can include text, images, YouTube links, and other links. YouTube links will be automatically embedded as videos."
                  maxLength={7000}
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                )}
                <div className="mt-2 text-sm text-muted-foreground">
                  <p>
                    <strong>Tips:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 mt-1">
                    <li>Paste YouTube URLs to automatically embed videos</li>
                    <li>Use formatting tools for better presentation</li>
                  </ul>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForm}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  id="SR-submit-resource-button"
                  type="button"
                  onClick={
                    editingResource ? handleEditResource : handleAddResource
                  }
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="border-white" />
                      {editingResource ? "Updating..." : "Adding..."}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      {editingResource ? "Update Resource" : "Add Resource"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-foreground">
                Preview: {previewResource.title}
              </h2>
              <button
                onClick={() => setPreviewResource(null)}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>•</span>
                  <span>{formatDate(previewResource.createdAt)}</span>
                </div>
              </div>

              <div className="prose max-w-none">
                <div
                  dangerouslySetInnerHTML={{
                    __html: processYouTubeContent(previewResource.content),
                  }}
                  className="text-muted-foreground"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteResource}
        title="Delete Resource"
        message={`Are you sure you want to delete "${deleteModal.resourceTitle}"? This action cannot be undone.`}
        confirmText="Delete Resource"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteModal.isLoading}
      />

      {/* YouTube Embed Styles */}
      <style>{`
        .youtube-embed-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          height: 0;
          margin: 16px 0 !important;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .youtube-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 8px;
        }
        
        /* Ensure YouTube embeds are properly contained within the prose container */
        .prose .youtube-embed-wrapper {
          margin: 16px 0 !important;
          max-width: 100% !important;
        }
        
        /* Ensure images are also properly sized */
        .prose img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 8px;
          margin: 8px 0;
        }
        
        @media (max-width: 640px) {
          .youtube-embed-wrapper {
            padding-bottom: 65%; /* Slightly taller on mobile for better viewing */
            margin: 8px 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
